import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { queryCache, useMutation } from "react-query";

import { Button, IconButton, showNotification } from "fe-shared/src/components";
import { CloseIcon, EditIcon, FlagIcon } from "fe-shared/src/components/Icons";
import { FlagDialog, FlagPayload } from "fe-shared-requests/src/components";

import {
  REQUESTS_QUERY_KEY,
  REQUEST_ACTIVITIES_QUERY_KEY,
  REQUESTS_NAVIGATION_QUERY_KEY,
  REQUEST_LIST_INFO_QUERY_KEY,
} from "consts";

import * as api from "./modules/api";

import styles from "./PickupRequestFlagButton.module.scss";

interface Props {
  canFlag: boolean;
  requestNumber: string;
  isFlagged: boolean;
  flagComment: string | null;
  showFlagToParticipants: boolean;
  /** "toggle" is the header control; "edit" is the pencil inside the flag banner. */
  mode?: "toggle" | "edit";
}

const PickupRequestFlagButton = (props: Props) => {
  const {
    canFlag,
    requestNumber,
    isFlagged,
    flagComment,
    showFlagToParticipants,
    mode = "toggle",
  } = props;
  const { t } = useTranslation();
  const [isDialogActive, setIsDialogActive] = useState(false);
  const [isUnflagHovered, setIsUnflagHovered] = useState(false);
  const [isUnflagFocused, setIsUnflagFocused] = useState(false);

  const onSettled = () => {
    queryCache.invalidateQueries(REQUESTS_QUERY_KEY);
    queryCache.invalidateQueries([REQUEST_ACTIVITIES_QUERY_KEY, requestNumber]);
    queryCache.invalidateQueries(REQUESTS_NAVIGATION_QUERY_KEY);
    queryCache.invalidateQueries(REQUEST_LIST_INFO_QUERY_KEY);
  };

  const onFailure = () => {
    showNotification(
      t("pickupRequests.details.flagError"),
      undefined,
      CloseIcon
    );
  };

  const [flag, flagResult] = useMutation(
    (payload: FlagPayload) => api.flagPickupRequest(requestNumber, payload),
    {
      onSuccess: () => {
        setIsDialogActive(false);
        onSettled();
        showNotification(t("pickupRequests.details.flagSuccess"));
      },
      onError: onFailure,
    }
  );

  const [unflag, unflagResult] = useMutation(
    () => api.unflagPickupRequest(requestNumber),
    {
      onSuccess: () => {
        onSettled();
        showNotification(t("pickupRequests.details.flagSuccess"));
      },
      onError: onFailure,
    }
  );

  if (!canFlag) {
    return null;
  }

  const isSubmitting = flagResult.isLoading || unflagResult.isLoading;
  // Hover and focus both arm the undo affordance, so the control is reachable by keyboard.
  const isUnflagActive = isUnflagHovered || isUnflagFocused;

  if (mode === "edit") {
    if (!isFlagged) {
      return null;
    }

    return (
      <>
        <IconButton
          hasTooltip
          id="request-edit-flag-button"
          className={styles.action}
          aria-label={t("pickupRequests.flag.editFlag")}
          disabled={isSubmitting}
          onClick={() => setIsDialogActive(true)}
        >
          <EditIcon fillColor={null} />
        </IconButton>
        <FlagDialog
          isEdit
          isActive={isDialogActive}
          comment={flagComment ?? ""}
          showToParticipants={showFlagToParticipants}
          isSubmitting={isSubmitting}
          onSubmit={flag}
          onCancel={() => setIsDialogActive(false)}
        />
      </>
    );
  }

  if (isFlagged) {
    return (
      <Button
        variant="inline"
        id="request-unflag-button"
        className={styles.flagged}
        disabled={isSubmitting}
        iconComponent={FlagIcon}
        onBlur={() => setIsUnflagFocused(false)}
        onFocus={() => setIsUnflagFocused(true)}
        onMouseLeave={() => setIsUnflagHovered(false)}
        onMouseEnter={() => setIsUnflagHovered(true)}
        onClick={() => unflag()}
      >
        {t(`pickupRequests.flag.${isUnflagActive ? "unflag" : "flagged"}`)}
      </Button>
    );
  }

  return (
    <>
      <IconButton
        hasTooltip
        id="request-add-flag-button"
        className={styles.action}
        aria-label={t("pickupRequests.flag.addFlag")}
        disabled={isSubmitting}
        onClick={() => setIsDialogActive(true)}
      >
        <FlagIcon fillColor={null} />
      </IconButton>
      <FlagDialog
        isActive={isDialogActive}
        comment=""
        showToParticipants={false}
        isSubmitting={isSubmitting}
        onSubmit={flag}
        onCancel={() => setIsDialogActive(false)}
      />
    </>
  );
};

export default PickupRequestFlagButton;
